class DetailsDisclosure extends HTMLElement {
    constructor() {
        super();
        this.mainDetailsToggle = this.querySelector('details');
        this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

        this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
        this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
    }

    onFocusOut() {
        setTimeout(() => {
            if (!this.contains(document.activeElement)) this.close();
        });
    }

    onToggle() {
        if (!this.animations) this.animations = this.content.getAnimations();

        if (this.mainDetailsToggle.hasAttribute('open')) {
            this.animations.forEach((animation) => animation.play());
        } else {
            this.animations.forEach((animation) => animation.cancel());
        }
    }

    close() {
        this.mainDetailsToggle.removeAttribute('open');
        this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
    }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
    constructor() {
        super();
        this.header = document.querySelector('.header-wrapper');
        this.stickyHeader = document.querySelector('sticky-header');
        this.isHoverEnabled = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

        this.hoverCloseTimeout = null;
        this.closeAnimationFallbackTimeout = null;
        this.transitionListener = null;
        this.isOpen = this.mainDetailsToggle.hasAttribute('open');

        this.onPointerEnterHandler = this.onPointerEnter.bind(this);
        this.onPointerLeaveHandler = this.onPointerLeave.bind(this);

        if (this.isHoverEnabled) {
            this.mainDetailsToggle.addEventListener('pointerenter', this.onPointerEnterHandler);
            this.mainDetailsToggle.addEventListener('pointerleave', this.onPointerLeaveHandler);
        }
    }

    get transparentHeader() {
        return document.querySelector(
            '.slideshow--transparent-header, .p80-hero--transparent-header'
        );
    }

    updateTransparentHeaderClass() {
        if (!this.stickyHeader) return;

        const anyMegaMenuVisible = Boolean(
            document.querySelector('header-menu .mega-menu--visible')
        );

        this.stickyHeader.classList.toggle('header-wrapper--transparent', anyMegaMenuVisible);
    }

    clearTransitionEvents() {
        if (this.content && this.transitionListener) {
            this.content.removeEventListener('transitionend', this.transitionListener);
            this.transitionListener = null;
        }
        clearTimeout(this.closeAnimationFallbackTimeout);
    }

    onPointerEnter(event) {
        if (!this.isHoverEnabled || event.pointerType !== 'mouse') return;

        clearTimeout(this.hoverCloseTimeout);
        this.open();
    }

    onPointerLeave(event) {
        if (!this.isHoverEnabled || event.pointerType !== 'mouse') return;

        this.hoverCloseTimeout = setTimeout(() => {
            if (this.mainDetailsToggle.matches(':hover')) return;
            if (this.contains(document.activeElement)) return;
            this.close();
        }, 120);
    }

    onToggle() {
        super.onToggle();
        if (!this.header) return;
        this.header.preventHide = this.mainDetailsToggle.open;

        if (
            document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !==
            ''
        )
            return;
        document.documentElement.style.setProperty(
            '--header-bottom-position-desktop',
            `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
        );

        // If a user opens/closes via click/keyboard, keep the "visible" class aligned.
        if (this.mainDetailsToggle.hasAttribute('open')) this.show();
        else this.hide();
    }

    open() {
        if (this.isOpen) return;

        this.isOpen = true;
        this.clearTransitionEvents();

        this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', true);
        this.mainDetailsToggle.setAttribute('open', '');

        requestAnimationFrame(() => {
            this.show();
        });
    }

    show() {
        this.mainDetailsToggle.classList.add('mega-menu--visible');
        if (this.transparentHeader) this.updateTransparentHeaderClass();
    }

    hide() {
        this.mainDetailsToggle.classList.remove('mega-menu--visible');
        if (this.transparentHeader) this.updateTransparentHeaderClass();
    }

    close() {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (prefersReducedMotion) {
            this.isOpen = false;
            this.clearTransitionEvents();
            this.hide();
            super.close();
            return;
        }

        if (!this.isOpen) return;

        this.isOpen = false;
        this.clearTransitionEvents();

        const summary = this.mainDetailsToggle.querySelector('summary');
        summary.setAttribute('aria-expanded', false);
        this.hide();

        this.transitionListener = () => {
            super.close();
            this.clearTransitionEvents();
        };

        this.content.addEventListener('transitionend', this.transitionListener, { once: true });

        this.closeAnimationFallbackTimeout = setTimeout(() => {
            super.close();
            this.clearTransitionEvents();
        }, 250);
    }

    disconnectedCallback() {
        clearTimeout(this.hoverCloseTimeout);
        this.clearTransitionEvents();

        if (this.isHoverEnabled) {
            this.mainDetailsToggle.removeEventListener('pointerenter', this.onPointerEnterHandler);
            this.mainDetailsToggle.removeEventListener('pointerleave', this.onPointerLeaveHandler);
        }
    }
}

customElements.define('header-menu', HeaderMenu);
